import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { REASON_MAX, validateReason } from '@/components/tenants/TenantStatusDialogs';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormAlert } from '@/components/ui/form-alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { adminKeys, adminsApi, type AdminDetail, type MfaCredentials } from '@/lib/admins-api';
import { genericErrorMessage, httpStatus, serverCode } from '@/lib/api-errors';

export const LAST_ACTIVE_ADMIN_MESSAGE = "Impossible, c'est le dernier admin actif";
const SELF_ACTION_MESSAGE = 'Action impossible sur votre propre compte';

// Erreurs signalées par un toast, modal fermée : la protection anti-brickage et les gardes self (boutons masqués sur
// son propre compte, ces codes ne devraient arriver que par une course ou un appel direct)
const TOAST_CODES: Record<string, string> = {
  LAST_ACTIVE_ADMIN: LAST_ACTIVE_ADMIN_MESSAGE,
  CANNOT_SUSPEND_SELF: SELF_ACTION_MESSAGE,
  CANNOT_DELETE_SELF: SELF_ACTION_MESSAGE,
  CANNOT_RESET_OWN_MFA: SELF_ACTION_MESSAGE,
};

/** Message affiché dans la modal pour les autres erreurs (statut changé entre-temps, admin supprimé, réseau). */
export const adminActionErrorMessage = (err: unknown): string => {
  switch (serverCode(err)) {
    case 'ADMIN_SUSPENDED':
      return "Cet admin est suspendu : réactivez-le avant de réinitialiser son MFA";
    case 'ADMIN_ALREADY_SUSPENDED':
      return 'Cet admin est déjà suspendu (la page a été actualisée)';
    case 'ADMIN_NOT_SUSPENDED':
      return "Cet admin n'est plus suspendu (la page a été actualisée)";
  }
  if (httpStatus(err) === 404) return 'Admin introuvable : il a peut-être été supprimé';
  return genericErrorMessage(err);
};

type Target = Pick<AdminDetail, 'id' | 'email'>;

interface DialogProps {
  admin: Target;
  open: boolean;
  onOpenChange(open: boolean): void;
}

/** Succès : toast + fermeture ; erreur « toast » : toast + fermeture ; toute erreur recharge l'admin (statut à jour). */
const useAdminMutation = <V, R>(fn: (v: V) => Promise<R>, opts: { success?: string; onSuccess?: (r: R) => void; close: () => void }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (result) => {
      if (opts.success) toast.success(opts.success);
      opts.close();
      opts.onSuccess?.(result);
      await queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
    onError: async (err) => {
      const code = serverCode(err);
      if (code && TOAST_CODES[code]) {
        toast.error(TOAST_CODES[code]);
        opts.close();
      }
      await queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
};

const ToastAwareAlert = ({ error }: { error: unknown }) => {
  const code = serverCode(error);
  if (code && TOAST_CODES[code]) return null;
  return <FormAlert>{adminActionErrorMessage(error)}</FormAlert>;
};

export const SuspendAdminDialog = ({ admin, open, onOpenChange }: DialogProps) => {
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const mutation = useAdminMutation((r: string) => adminsApi.suspend(admin.id, r), {
    success: `Admin ${admin.email} suspendu`,
    close: () => reset(false),
  });

  const reset = (next: boolean) => {
    if (!next) {
      setReason('');
      setFieldError(null);
      mutation.reset();
    }
    onOpenChange(next);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    // Facultatif côté API, imposé par l'UI (diagnostic 9.6b, point 5)
    const invalid = validateReason(reason);
    setFieldError(invalid);
    if (!invalid) mutation.mutate(reason.trim());
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && reset(next)}>
      <DialogContent>
        <form onSubmit={submit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Suspendre l'admin</DialogTitle>
            <DialogDescription>
              <span className="font-semibold text-foreground">{admin.email}</span> perd l'accès immédiatement, sessions en cours
              comprises. L'action est tracée et réversible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="admin-suspend-reason">Motif de la suspension</Label>
            <Textarea
              id="admin-suspend-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex. : départ de l'entreprise"
              maxLength={REASON_MAX}
              autoFocus
              aria-invalid={Boolean(fieldError)}
              aria-describedby={fieldError ? 'admin-suspend-reason-error' : undefined}
            />
            <div className="flex justify-between text-xs">
              <span id="admin-suspend-reason-error" className="text-danger">
                {fieldError}
              </span>
              <span className="text-muted-foreground tabular-nums">
                {reason.trim().length}/{REASON_MAX}
              </span>
            </div>
          </div>
          {mutation.isError && <ToastAwareAlert error={mutation.error} />}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => reset(false)} disabled={mutation.isPending}>
              Annuler
            </Button>
            <Button type="submit" variant="destructive" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Confirmer la suspension
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ReactivateAdminDialog = ({ admin, open, onOpenChange, mfaEnabled }: DialogProps & { mfaEnabled: boolean }) => {
  const mutation = useAdminMutation(() => adminsApi.reactivate(admin.id), {
    success: `Admin ${admin.email} réactivé`,
    close: () => reset(false),
  });

  const reset = (next: boolean) => {
    if (!next) mutation.reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && reset(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Réactiver l'admin</DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-foreground">{admin.email}</span> pourra se reconnecter.{' '}
            {mfaEnabled
              ? 'Il repasse actif.'
              : "Son MFA n'a jamais été validé : il repasse invité et deviendra actif à sa première connexion."}
          </DialogDescription>
        </DialogHeader>
        {mutation.isError && <ToastAwareAlert error={mutation.error} />}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => reset(false)} disabled={mutation.isPending}>
            Annuler
          </Button>
          <Button
            type="button"
            className="bg-success text-white hover:bg-success/90"
            onClick={() => mutation.mutate(undefined)}
            disabled={mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            Confirmer la réactivation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Confirmation seulement : les nouveaux secrets sont remis à l'appelant (onReset), qui ouvre la modal credentials. */
export const ResetMfaDialog = ({ admin, open, onOpenChange, onReset }: DialogProps & { onReset(credentials: MfaCredentials): void }) => {
  const mutation = useAdminMutation(() => adminsApi.resetMfa(admin.id), {
    close: () => reset(false),
    onSuccess: onReset,
  });

  // reset() vide aussi la réponse (secrets) gardée par la mutation
  const reset = (next: boolean) => {
    if (!next) mutation.reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && reset(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Réinitialiser le MFA</DialogTitle>
          <DialogDescription>
            Vraiment réinitialiser le MFA de <span className="font-semibold text-foreground">{admin.email}</span> ? L'admin devra
            reconfigurer son application TOTP. Ses anciens codes de secours ne marcheront plus et ses sessions en cours sont
            coupées (il repasse invité jusqu'à sa prochaine connexion).
          </DialogDescription>
        </DialogHeader>
        {mutation.isError && <ToastAwareAlert error={mutation.error} />}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => reset(false)} disabled={mutation.isPending}>
            Annuler
          </Button>
          <Button type="button" variant="destructive" onClick={() => mutation.mutate(undefined)} disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            Réinitialiser le MFA
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** L'email est stocké en minuscules : comparaison insensible à la casse et aux espaces autour. */
export const emailMatches = (typed: string, email: string): boolean => typed.trim().toLowerCase() === email.toLowerCase();

export const DeleteAdminDialog = ({ admin, open, onOpenChange }: DialogProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [typed, setTyped] = useState('');
  const mutation = useAdminMutation(() => adminsApi.remove(admin.id), {
    success: `Admin ${admin.email} supprimé`,
    close: () => reset(false),
    onSuccess: () => {
      // Le détail répondrait 404 : retiré du cache avant de quitter la page
      queryClient.removeQueries({ queryKey: adminKeys.detail(admin.id) });
      void navigate({ to: '/admins', replace: true });
    },
  });

  const reset = (next: boolean) => {
    if (!next) {
      setTyped('');
      mutation.reset();
    }
    onOpenChange(next);
  };

  const matches = emailMatches(typed, admin.email);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (matches) mutation.mutate(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && reset(next)}>
      <DialogContent>
        <form onSubmit={submit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Supprimer l'admin</DialogTitle>
            <DialogDescription>
              <span className="font-semibold text-foreground">{admin.email}</span> disparaît de la liste et ne peut plus se connecter.
              Son historique d'audit est conservé. Cette action ne peut pas être annulée depuis l'interface.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="admin-delete-confirm">
              Pour confirmer, saisissez <span className="font-mono font-semibold">{admin.email}</span>
            </Label>
            <Input id="admin-delete-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoFocus />
          </div>
          {mutation.isError && <ToastAwareAlert error={mutation.error} />}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => reset(false)} disabled={mutation.isPending}>
              Annuler
            </Button>
            <Button type="submit" variant="destructive" disabled={!matches || mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
